import { connect } from './client.js';
import { TRYPORTALError } from './errors.js';
import { toLeaderboardPage } from './leaderboard.js';
export const STANDALONE_CHANNEL = 'traident-standalone-v1';
/** Read the real public board even before a player chooses to connect. */
export async function readPublicLeaderboard(options) {
    const request = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    const base = new URL(options.apiBaseUrl ?? 'https://api.app.traident.jp').origin;
    const rankingTrack = options.rankingTrack ?? 'STANDARD';
    const limit = options.limit ?? 20;
    if (!Number.isInteger(limit) || limit < 1 || limit > 100)
        throw new TRYPORTALError('INVALID_USAGE', 'leaderboard limit must be 1–100');
    const get = async (path) => {
        const response = await request(base + path, {
            credentials: 'omit',
            headers: { accept: 'application/json' },
            signal: AbortSignal.timeout(15000),
        });
        if (!response.ok)
            throw new TRYPORTALError('LEADERBOARD_UNAVAILABLE', 'the platform did not return a leaderboard', { status: response.status, retryable: response.status >= 500 || response.status === 429 });
        return (await response.json());
    };
    try {
        const competitions = await get(`/v1/games/${encodeURIComponent(options.gameKey)}/competitions`);
        const matches = Array.isArray(competitions)
            ? competitions.filter((item) => typeof item === 'object' &&
                item !== null &&
                'rankingTrack' in item &&
                item.rankingTrack === rankingTrack)
            : [];
        const competition = options.competitionId
            ? matches.find((item) => item['competitionId'] === options.competitionId)
            : (matches.find((item) => item['state'] === 'ACTIVE') ??
                matches
                    .filter((item) => ['FINALIZED', 'ARCHIVED', 'FROZEN'].includes(String(item['state'])))
                    .sort((a, b) => String(b['startsAt']).localeCompare(String(a['startsAt'])))[0]);
        if (typeof competition?.['competitionId'] !== 'string')
            throw new TRYPORTALError('LEADERBOARD_UNAVAILABLE', 'no published competition is available for this game and ranking track');
        const competitionId = competition['competitionId'];
        const params = new URLSearchParams({ limit: String(limit) });
        if (options.cursor)
            params.set('cursor', options.cursor);
        return toLeaderboardPage(await get(`/v1/competitions/${encodeURIComponent(competitionId)}/leaderboard?${params}`), { competitionId, rankingTrack });
    }
    catch (error) {
        if (error instanceof TRYPORTALError)
            throw error;
        throw new TRYPORTALError('LEADERBOARD_UNAVAILABLE', 'the public leaderboard could not be reached', { retryable: true, cause: error });
    }
}
/** Call directly inside a player click/tap; no popup is opened during boot. */
export function connectStandalone(options) {
    const browser = options.browserWindow ?? (typeof window === 'undefined' ? undefined : window);
    if (!browser || browser.parent !== browser)
        return Promise.reject(new TRYPORTALError('INVALID_USAGE', 'Use connect() inside the TRAIDENT play shell; connectStandalone() is for a top-level game page.'));
    const platform = new URL(options.platformUrl ?? 'https://app.traident.jp');
    if (!(platform.protocol === 'https:' ||
        (platform.protocol === 'http:' &&
            ['localhost', '127.0.0.1', '[::1]'].includes(platform.hostname))))
        return Promise.reject(new TRYPORTALError('INVALID_USAGE', 'Use an HTTPS platform origin or localhost.'));
    const state = Array.from(browser.crypto.getRandomValues(new Uint8Array(32)), (b) => b.toString(16).padStart(2, '0')).join('');
    const url = new URL(`/sdk/connect/${encodeURIComponent(options.gameKey)}`, platform.origin);
    url.searchParams.set('origin', browser.location.origin);
    url.searchParams.set('state', state);
    return new Promise((resolve, reject) => {
        let popup = null;
        let done = false;
        let popupReady = false;
        const finish = (error, launch) => {
            if (done)
                return;
            done = true;
            clearTimeout(timeout);
            clearTimeout(handshakeTimeout);
            clearInterval(closedCheck);
            browser.removeEventListener('message', onMessage);
            popup?.close();
            if (error)
                reject(error);
            else if (launch) {
                const { gameKey: _gameKey, platformUrl: _platformUrl, authorizationTimeoutMs: _timeout, browserWindow: _window, ...connection } = options;
                void connect({ ...connection, launch }).then(resolve, reject);
            }
        };
        const onMessage = (event) => {
            if (event.source !== popup ||
                event.origin !== platform.origin ||
                event.data?.channel !== STANDALONE_CHANNEL ||
                event.data?.state !== state)
                return;
            if (event.data.type === 'READY') {
                popupReady = true;
                popup?.postMessage({ channel: STANDALONE_CHANNEL, type: 'HELLO', state }, platform.origin);
                return;
            }
            if (event.data.type !== 'CONNECTED' || event.data.gameKey !== options.gameKey)
                return;
            const launch = event.data.launch;
            if (!launch ||
                launch.rankingTrack !== 'STANDARD' ||
                launch.liveSessionId !== null ||
                typeof launch.sessionToken !== 'string' ||
                typeof launch.gameSessionId !== 'string' ||
                typeof launch.competitionId !== 'string' ||
                typeof launch.apiBaseUrl !== 'string' ||
                typeof launch.realtimeUrl !== 'string' ||
                typeof launch.refreshToken !== 'string') {
                finish(new TRYPORTALError('CONNECT_FAILED', 'the platform returned an invalid standalone session'));
                return;
            }
            finish(undefined, launch);
        };
        const timeout = setTimeout(() => finish(new TRYPORTALError('CONNECT_FAILED', 'TRAIDENT connection timed out. Try the connection button again.')), Math.min(300000, options.authorizationTimeoutMs ?? 120000));
        const handshakeTimeout = setTimeout(() => {
            if (!popupReady)
                finish(new TRYPORTALError('CONNECT_FAILED', 'This browser could not open the TRAIDENT connection window. Open the game on TRAIDENT instead.', {
                    detail: {
                        playUrl: new URL(`/play/${encodeURIComponent(options.gameKey)}`, platform.origin)
                            .href,
                    },
                }));
        }, 10000);
        const closedCheck = setInterval(() => {
            if (popup?.closed)
                finish(new TRYPORTALError('CONNECT_FAILED', 'TRAIDENT connection was cancelled.'));
        }, 500);
        browser.addEventListener('message', onMessage);
        popup = browser.open(url.toString(), '_blank', 'popup,width=620,height=720');
        if (!popup)
            finish(new TRYPORTALError('CONNECT_FAILED', 'Allow the TRAIDENT connection popup, then press the connection button again.'));
    });
}
